import jsonExport from "jsonexport/dist";
import { Download } from "lucide-react";
import {
  downloadCSV,
  useDataProvider,
  useNotify,
  useRecordContext,
  useTranslate,
} from "ra-core";
import { useState } from "react";
import { Button } from "@/components/ui/button";

import type { ChampClass, ClassRosterEntry } from "../types";
import { ROSTER_STAGES, toRosterExportRows } from "./classUtils";

const MAX_ROSTER_ROWS = 1000;

/** Downloads the class roster as a CSV that opens cleanly in Excel. */
export const RosterExportButton = () => {
  const record = useRecordContext<ChampClass>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const translate = useTranslate();
  const [isExporting, setIsExporting] = useState(false);

  if (!record) return null;

  const handleClick = async () => {
    setIsExporting(true);
    try {
      const { data } = await dataProvider.getList<ClassRosterEntry>(
        "class_roster",
        {
          filter: { class_id: record.id },
          sort: { field: "player_last_name", order: "ASC" },
          pagination: { page: 1, perPage: MAX_ROSTER_ROWS },
        },
      );
      if (!data.length) {
        notify("resources.classes.roster.export_empty", { type: "info" });
        return;
      }
      const stageOrder = (stage: string) =>
        ROSTER_STAGES.indexOf(stage as (typeof ROSTER_STAGES)[number]);
      const sorted = [...data].sort(
        (a, b) => stageOrder(a.stage) - stageOrder(b.stage),
      );
      jsonExport(
        toRosterExportRows(sorted, record.name),
        {},
        (error: Error | null, csv: string) => {
          if (error) {
            notify(error.message, { type: "error" });
            return;
          }
          downloadCSV(csv, `roster-${record.name}`);
        },
      );
    } catch (error) {
      notify((error as Error).message, { type: "error" });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleClick}
      disabled={isExporting}
    >
      <Download className="h-4 w-4" />
      {translate("resources.classes.action.export_roster")}
    </Button>
  );
};
